import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { parse, print } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import {
  DynamoDbOperations,
  DynamoDbTables,
  Source,
} from "../../../common/Source";
import { EMPTY_VALUE, FieldValues, Schema } from "../../../Schema";
import { GraphqlManager } from "../../../graphql/GraphqlManager";
import { DataTransformManager, DataTransformDirections } from "../../../data/DataTransformManager";

/**
 * Entry getters shared by equipment and cluster. Both are the same GraphQL type
 * (`DigitalLabInstrumentRepositoryEntry`, fetched via
 * `getDigitalLabInstrumentRepositoryEntry`), so the query mechanics live here and
 * the per-entity files (`Equipment.ts`, `Cluster.ts`) are thin, well-named
 * wrappers — mirroring how `getAndProcessX` wraps `getX` elsewhere.
 *
 * Unlike the reference/master-data getters (Rooms, LinkedInstances, ...), the
 * selection set is not hardcoded: it is derived from the passed `Schema` via
 * `GraphqlManager` (the `Selection` fragment), so the fetched fields stay in sync
 * with the schema. The `getAndProcess*` variants additionally shape the raw
 * record into form values with `DataTransformManager` (`FROM_GRAPHQL`).
 */

type GetEntryVariables = {
  id: string;
};

/**
 * Builds the `Selection` fragment (as GraphQL text) for the given schema.
 * Returns an empty string when the schema yields no selectable fields.
 */
export const buildSelectionFragment = async (schema: Schema): Promise<string> => {
  const fragment = (await new GraphqlManager(schema).init({}))
    .getCustomState().selectionFragment;
  return fragment ? print(fragment) : "";
};

/**
 * Builds the `getDigitalLabInstrumentRepositoryEntry(id)` query document, with its
 * selection set derived from the schema.
 */
export const buildGetEntryQuery = async (
  schema: Schema
): Promise<TypedDocumentNode<FieldValues, GetEntryVariables>> => {
  const selectionFragment = await buildSelectionFragment(schema);
  return parse(`
    query getDigitalLabInstrumentRepositoryEntry($id: ID!) {
      getDigitalLabInstrumentRepositoryEntry(id: $id) {
        ...Selection
      }
    }
    ${selectionFragment}
  `);
};

/**
 * Builds the source for a single entry.
 *
 * The GraphQL half has to be built rather than declared, because its selection set comes
 * from the schema. The DynamoDB half is a plain fetch by id: an item comes back whole, so
 * there is no selection to derive and nothing about it varies with the schema. The
 * `getAndProcess*` wrappers narrow both to the same shape.
 *
 * @param schema - The schema whose fields the GraphQL half selects.
 * @returns The source.
 */
export const buildEntrySource = async (
  schema: Schema
): Promise<Source<FieldValues, GetEntryVariables>> => ({
  graphql: await buildGetEntryQuery(schema),
  dynamodb: {
    operation: DynamoDbOperations.GET,
    table: DynamoDbTables.ENTRIES,
    key: ({ id }) => ({ id }),
  },
});

/**
 * Fetches a single entry by id and returns the raw record.
 */
export const getEntry = async (
  client: SchemaClient,
  schema: Schema,
  id: string
): Promise<FieldValues | typeof EMPTY_VALUE> => {
  return await client.query(await buildEntrySource(schema), { id });
};

/**
 * Fetches a single entry by id and maps it into form values via the schema's
 * `FROM_GRAPHQL` data transforms. Returns `{}` when the entry is not found.
 */
export const getAndProcessEntry = async (
  client: SchemaClient,
  schema: Schema,
  id: string
): Promise<FieldValues> => {
  const entry = await getEntry(client, schema, id);
  if (!entry) {
    return {};
  }
  return await new DataTransformManager(schema).transformValues(
    DataTransformDirections.FROM_GRAPHQL,
    entry
  );
};
